import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from "@/lib/money"
import { cn } from "@/lib/utils"

interface ProcessorMetricsCardProps {
    processor: ProcessorDetails
}

const formatPercentage = (value: number | null) => {
    if (value === null) return 'N/A'
    return `${(value * 100).toFixed(2)}%`
}

const formatAmount = (amountMinorUnits: number, currency: string | null | undefined) =>
    currency
        ? formatMoney(amountMinorUnits, currency)
        : (amountMinorUnits / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const ProcessorMetricsCard = ({ processor }: ProcessorMetricsCardProps) => {
    const { t } = useTranslation('processors')

    const hasMetrics = processor.totalProcessedAmount !== null ||
        processor.monthlyProcessingCapacity !== null ||
        processor.approvalRate !== null ||
        processor.monthlyApprovalRate !== null

    if (!hasMetrics) {
        return null
    }

    // The cap is stored in the processor's base currency, the volumes in each payment's currency.
    const capacityCurrency = processor.baseCurrency
    const processedVolumes = processor.processedVolumes?.length
        ? processor.processedVolumes
        : [{ currency: processor.baseCurrency, amount: processor.totalProcessedAmount ?? 0 }]

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('performanceMetrics')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {processor.totalProcessedAmount !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('totalProcessed')}</p>
                            {processedVolumes.map((volume) => (
                                <p key={volume.currency ?? ''} className="text-2xl font-bold">
                                    {formatAmount(volume.amount, volume.currency)}
                                </p>
                            ))}
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyCapacity')}</p>
                            <p className="text-2xl font-bold">
                                {formatAmount(processor.monthlyProcessingCapacity, capacityCurrency)}
                            </p>
                        </div>
                    )}

                    {processor.approvalRate !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('approvalRate')}</p>
                            <p className="text-2xl font-bold">
                                {formatPercentage(processor.approvalRate)}
                            </p>
                        </div>
                    )}

                    {processor.monthlyApprovalRate !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyApprovalRate')}</p>
                            <p className="text-2xl font-bold">
                                {formatPercentage(processor.monthlyApprovalRate)}
                            </p>
                        </div>
                    )}
                </div>

                {processor.capacityUsagePercentage !== null && processor.capacityUsagePercentage !== undefined && (
                    <div className="mt-6">
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">{t('capacityUtilization')}</p>
                            <p
                                className={cn(
                                    'text-sm font-semibold',
                                    processor.capacityUsagePercentage >= 100
                                        ? 'text-destructive'
                                        : processor.capacityUsagePercentage >= 90
                                            ? 'text-amber-600'
                                            : 'text-foreground',
                                )}
                            >
                                {processor.capacityUsagePercentage.toFixed(2)}%
                            </p>
                        </div>
                        <Progress
                            value={Math.min(100, processor.capacityUsagePercentage)}
                            className="h-2.5 bg-gray-200 dark:bg-gray-700"
                        />
                        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                                {t('usedThisMonth', {
                                    amount: formatAmount(processor.monthSuccessVolume ?? 0, capacityCurrency),
                                })}
                            </span>
                            {processor.remainingCapacity !== null && processor.remainingCapacity !== undefined && (
                                <span>
                                    {processor.remainingCapacity >= 0
                                        ? t('remainingCapacity', {
                                            amount: formatAmount(processor.remainingCapacity, capacityCurrency),
                                        })
                                        : t('overCapacity', {
                                            amount: formatAmount(-processor.remainingCapacity, capacityCurrency),
                                        })}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

